/**
 * The case file page (docs/19 A3, part 5) — what the app knows: the decisions,
 * his particulars, the history and the papers. What needs a look is no longer
 * listed here: it is worked out on the server and shown at the top of the case
 * page, only when something needs the officer (Phase A, A10 — `NeedsALook`).
 *
 * **Printed, not kept** (docs/18 §6): every line here is read off the record
 * the moment the page loads — a decision with who took it, when, and the signed
 * paper it came from; a charge with his answer; a note with who noted it. The
 * page states what is on the record and nothing it would have to infer.
 *
 * *Record something else* keeps every act the card does not happen to be
 * offering today — service on any document, a decision at any fork, a fact a
 * rule reads — because a card is one answer, and an officer holding a paper
 * the card did not expect must still be able to put it on the record.
 */

import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { api } from '../../api/client'
import type { CaseDetail, IntakeEnglishView } from '../../api/client'
import { ErrorEnvelope } from '../../components/bits'
import {
  RecordApproval,
  RecordDecisions,
  RecordFact,
  RecordReply,
  RecordService,
  Timeline,
} from '../../components/acts'
import { readDate } from '../../lib/period'
import { useRefreshCase } from './acting'
import { DecideOffer } from './NextActionCard'
import { Particulars } from './Particulars'

export function CaseFile({
  data,
  readings,
  readOnly,
}: {
  data: CaseDetail
  readings: IntakeEnglishView | null
  readOnly: boolean
}) {
  return (
    <section className="case-file" data-testid="case-file">
      <h3>The case file</h3>
      <div className="cf-grid">
        <Particulars data={data} readings={readings} readOnly={readOnly} />
        <Decisions data={data} />
        <Charges data={data} />
        <Notes data={data} />
      </div>
      <Papers data={data} />
      <Timeline data={data} />
      {!readOnly ? <RecordSomethingElse data={data} /> : null}
    </section>
  )
}

/* ------------------------------------------------------------- decisions -- */

function Decisions({ data }: { data: CaseDetail }) {
  const rows = data.recorded_decisions ?? []
  return (
    <div className="cf-block" data-testid="cf-decisions">
      <h4>Decisions</h4>
      {rows.length === 0 ? (
        <p className="small muted">No decision is on the record yet.</p>
      ) : (
        <ul className="cf-list">
          {rows.map((row) => (
            <li key={row.key} data-testid={`cf-decision-${row.key}`}>
              <span className="cf-what">{row.label}</span>
              <span className="cf-answer">{row.outcome_label}</span>
              <span className="small muted">
                {row.decided_on ? readDate(row.decided_on) : ''}
                {row.actor ? ` · recorded by ${row.actor}` : ''}
              </span>
              {row.source_file_id ? (
                <a
                  className="small mono"
                  href={api.sourceFilePath(data.id, row.source_file_id)}
                  target="_blank"
                  rel="noreferrer"
                >
                  {row.source_filename ?? 'the signed paper'}
                </a>
              ) : (
                <span className="small faint">no signed paper attached</span>
              )}
              {/* **The way back from a wrong answer.** The penalty proposed and
                  the disagreement have no writer but their decision, so the
                  decision itself is offered again — the server's choices, none
                  picked, dated as the order on the record is. */}
              {row.again ? (
                <details className="cf-again" data-testid={`cf-again-${row.key}`}>
                  <summary className="small">Record it again</summary>
                  <DecideOffer data={data} offer={row.again} />
                </details>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/* --------------------------------------------------------------- charges -- */

function Charges({ data }: { data: CaseDetail }) {
  const charges = data.charges ?? []
  if (charges.length === 0 && !data.proposed_penalty) return null
  return (
    <div className="cf-block" data-testid="cf-charges">
      <h4>The charges</h4>
      <ol className="cf-charges">
        {charges.map((charge) => (
          <li key={charge.ordinal}>
            <span>{charge.heading}</span>{' '}
            <span
              className={`small ${charge.answer === 'not_admitted' ? 'warn-text' : 'muted'}`}
            >
              {/* His answer, and where a charge he did not admit goes, in the
                  server's words: *goes to the inquiry* is true only where one
                  is held, and this screen cannot know that (walkthrough 32.34).
                  The fallback says only what the answer itself says. */}
              —{' '}
              {charge.answer_label ??
                (charge.answer === 'admitted'
                  ? 'admitted'
                  : charge.answer === 'not_admitted'
                    ? 'not admitted'
                    : 'his answer is not on the record')}
            </span>
          </li>
        ))}
      </ol>
      {data.proposed_penalty ? (
        <p className="small">
          Penalty proposed: {data.proposed_penalty.label}{' '}
          <span className="mono faint">{data.proposed_penalty.rule}</span>
          {data.proposed_penalty.inquiry_mandatory ? (
            <span className="warn-text"> — an inquiry is mandatory (Rule 16(1-A))</span>
          ) : null}
        </p>
      ) : null}
    </div>
  )
}

/* ----------------------------------------------------------------- notes -- */

function Notes({ data }: { data: CaseDetail }) {
  const notes = data.notes ?? []
  if (notes.length === 0) return null
  return (
    <div className="cf-block" data-testid="cf-notes">
      <h4>Noted, not on paper</h4>
      <ul className="cf-list">
        {notes.map((note, index) => (
          <li key={index}>
            <span>{note.text}</span>
            <span className="small muted">
              {note.noted_on ? readDate(note.noted_on) : ''}
              {note.actor ? ` · ${note.actor}` : ''}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/* ---------------------------------------------------------------- papers -- */

function Papers({ data }: { data: CaseDetail }) {
  const papers = data.papers_on_file ?? []
  return (
    <div className="cf-papers" data-testid="cf-papers">
      <h4>
        Papers on the file <span className="cnt mono">{papers.length}</span>
      </h4>
      {papers.length === 0 ? (
        <p className="small muted">No paper is on the file yet.</p>
      ) : (
        <ul className="cf-list">
          {papers.map((paper) => (
            <li key={paper.id}>
              <span className="mono faint">{paper.ordinal}</span>
              <a
                className="mono small"
                href={api.sourceFilePath(data.id, paper.id)}
                target="_blank"
                rel="noreferrer"
              >
                {paper.filename}
              </a>
              <span className="small">
                {paper.kind_label ??
                  (paper.kind_detected_label
                    ? `looks like ${paper.kind_detected_label} — not confirmed`
                    : 'what it is has not been said')}
              </span>
              <span className="small muted">
                {paper.document_date ? readDate(paper.document_date) : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/* -------------------------------------------------- record something else -- */

function RecordSomethingElse({ data }: { data: CaseDetail }) {
  return (
    <details className="record-else" data-testid="record-something-else">
      <summary>Record something else</summary>
      <p className="small muted">
        Every act the case can take, whatever the card is showing today — for a paper that
        arrived out of order, or an act done on paper before this case was in the app.
      </p>
      <section className="acts" data-testid="acts">
        <RecordService data={data} />
        <RecordDecisions data={data} />
        <RecordReply data={data} />
        <RecordApproval data={data} />
        <RecordFact data={data} />
      </section>
      {data.state === 'OPEN' ? <WithdrawCase data={data} /> : null}
    </details>
  )
}

/**
 * The authority withdrew the case (A5 row 39) — a decision with its reason,
 * and a state, never a deletion. The reason is the authority's, so it is typed:
 * this is the one act whose content no list can offer.
 */
function WithdrawCase({ data }: { data: CaseDetail }) {
  const refresh = useRefreshCase(data.id)
  const [open, setOpen] = useState(false)
  const [reason, setReason] = useState('')
  const [on, setOn] = useState(data.as_of)
  const withdraw = useMutation({
    mutationFn: () =>
      api.withdrawCase(data.id, {
        preflight: false,
        reason: reason.trim(),
        withdrawn_on: on,
        source_file_id: null,
      }),
    onSuccess: () => {
      setOpen(false)
      refresh()
    },
  })
  if (!open) {
    return (
      <div className="withdraw-case">
        <button
          type="button"
          className="quiet"
          onClick={() => setOpen(true)}
          data-testid="open-withdraw-case"
        >
          The authority withdrew the case
        </button>
      </div>
    )
  }
  return (
    <div className="blocked unsettled withdraw-case" data-testid="withdraw-case">
      <div style={{ width: '100%' }}>
        <div className="why">
          The case becomes withdrawn and stays on the register with every paper and act it has.
          The authority&apos;s reason goes on the record.
        </div>
        <label>
          The authority&apos;s reason
          <input
            aria-label="The authority's reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </label>
        <label className="na-date">
          Withdrawn on
          <input
            type="date"
            aria-label="Withdrawn on"
            value={on}
            onChange={(e) => setOn(e.target.value)}
          />
        </label>
        <div className="row" style={{ gap: 8, marginTop: 8 }}>
          <button
            type="button"
            disabled={reason.trim().length < 5 || !on || withdraw.isPending}
            onClick={() => withdraw.mutate()}
            data-testid="confirm-withdraw-case"
          >
            {withdraw.isPending ? 'Recording…' : 'Withdraw the case'}
          </button>
          <button type="button" className="quiet" onClick={() => setOpen(false)}>
            Keep it open
          </button>
        </div>
        <ErrorEnvelope error={withdraw.error} />
      </div>
    </div>
  )
}
